'use client';

import { useState } from 'react';
import { Heart } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useFavoritesStore } from '@/stores/favoritesStore';
import { startGuestFavorite } from '@/lib/favoriteIntent';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  listingId: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export default function FavoriteButton({
  listingId,
  className,
  size = 'md',
  showLabel = false,
}: FavoriteButtonProps) {
  const { user } = useAuthStore();
  const { isFavorite, toggleFavorite } = useFavoritesStore();

  const [isAnimating, setIsAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const favorited = isFavorite(listingId);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      // Remember the home + full URL; sign-in returns here and completes the save.
      startGuestFavorite(listingId);
      return;
    }

    if (isLoading) return;

    setIsAnimating(true);
    setIsLoading(true);

    try {
      await toggleFavorite(listingId);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    } finally {
      setIsLoading(false);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      className={cn(
        'glass relative flex items-center justify-center gap-2 rounded-full transition-all duration-200 hover:bg-glass-bright',
        'disabled:cursor-not-allowed disabled:opacity-50',
        !showLabel && sizeClasses[size],
        showLabel && 'px-4 py-2',
        isAnimating && 'scale-110',
        className
      )}
    >
      <Heart
        className={cn(
          iconSizes[size],
          'transition-all duration-200',
          favorited ? 'fill-coral-400 text-coral-400' : 'fill-transparent text-ink-soft',
          isAnimating && favorited && 'scale-125'
        )}
      />
      {showLabel && (
        <span
          className={cn('text-sm font-medium', favorited ? 'text-coral-300' : 'text-ink-soft')}
        >
          {favorited ? 'Saved' : 'Save'}
        </span>
      )}
    </button>
  );
}
